// Port of PROTO about.html (build.py "about" section): the clan's eight
// articles (quill-wipe reveal, once per item), the clan video banner (no
// iframe until clicked), and the Hall of Members player grids. No flag
// ring — Hall of Members is a plain centred `.h2` heading, as build.py has
// it today.
import { useEffect, useRef, useState } from 'react';
import type { SyntheticEvent } from 'react';
import type { MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { RosterResponse, RosterSection } from '@clan-heart/shared';
import { getRoster } from '../../api.js';
import { BannerCanvas } from '../BannerCanvas.js';
import { Opener } from '../Opener.js';
import { gsap, reduceMotion, useGsapContext, useReveal } from '../motion.js';
import type { PlayerRow } from './rosterFallback.js';
import { rosterRowsFor } from './rosterFallback.js';
import './AboutPage.css';

/**
 * Name -> `/crew/<slug>` for every roster entry in `section` that is linked
 * to an approved member with a profile slug. Kept separate from
 * `rosterRowsFor`/`PlayerRow` (owned elsewhere) so linking a name to its
 * public profile doesn't touch that shared fallback helper — this reads the
 * same loaded `roster`, or an empty map while it's loading/failed, which is
 * exactly when `rosterRowsFor` itself falls back to the hard-coded arrays
 * (those carry no slug, so their names never link).
 */
function profileLinksFor(section: RosterSection, roster: RosterResponse | undefined): Map<string, string> {
  const links = new Map<string, string>();
  if (!roster) return links;
  for (const entry of roster.sections[section]) {
    if (entry.profileSlug) links.set(entry.name, entry.profileSlug);
  }
  return links;
}

const VALUES = [
  'respect and trust is given by default',
  'we expect behaviour and communication of grown-ups',
  'playing games is not our first priority',
  'we don’t shy away from being competitive',
  'we have members from all corners of the world',
  'we like to help others and hope the kindness is paid forward',
  'contribution to the clan is completely voluntary but appreciated',
  'we welcome both new and experienced players into our ranks',
];
const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII'];

const F = (n: string) => `/art/flags/${n}.webp`;
const STD = F('flag-heart-standard-clean');
const HEART1 = F('flag-heart-1');

const OFFICERS: PlayerRow[] = [
  { name: 'Hartje', role: 'Clan Founder (No Boss)', img: HEART1 },
  { name: 'Minori van Eiksteen', role: 'Officer + Shipwright', img: F('minori-flag') },
  { name: 'Koldthorn', role: 'Officer + Naval Command', img: F('flag-koldhorn') },
  { name: 'Captain Pantaloons', role: 'Officer + Master of Coin', img: F('flag-hartje') },
];

const SPECIAL: Record<string, string> = {
  'Adm Frisian': HEART1,
  'Davi Jon': F('flag-squid-davi-jon-pastel'),
  Sicklet: F('sicklet'),
  'Eric Bloodhair': HEART1,
  Viventor: F('flag-viventor'),
  Sambala: F('flag-sambala'),
};

const MEMBER_NAMES = [
  'Adm Frisian',
  'Captain Rum Beard',
  'Horsey22',
  'Kogu',
  'Syakel',
  'Davi Jon',
  'Quellix',
  'Willis',
  'Mr Steel 2',
  'Hayes57',
  'Sicklet',
  'Constitution',
  'Tsavo',
  'FifiLongWind',
  'SaleeRover',
  'Tastes like Hell',
  'Lacoda',
  'Eric Bloodhair',
  'Mencio',
  'Viventor',
  'Sucker Punch Sally',
  'Jeffery O Skull',
  'Big Tallahasee',
  'Captain Jim',
  'Unsobermeerkat',
  'Joost van Dyke',
  'NoHoHanibal',
  'UCF Darkstar',
  'Sambala',
  'Maglore',
  'Draconic',
];
const MEMBERS: PlayerRow[] = MEMBER_NAMES.map((name) => ({ name, img: SPECIAL[name] ?? STD }));

const HONORABLE: PlayerRow[] = [
  { name: 'Captain Hellheim', role: 'Former officer (Retired 06/2025)', img: F('flag-hellheim') },
  { name: 'Heartless Queen', role: 'Clan Co-Founder (Retired 03/2025)', img: F('flag-heartlessqueen') },
  { name: 'Scorcer', role: 'Former Officer (Retired 03/2025)', img: HEART1 },
];

const DOCKMATES: PlayerRow[] = [
  { name: 'Abaddon', role: 'IMPRI', img: HEART1 },
  { name: 'Jamminram', role: 'BLACK', img: HEART1 },
  { name: 'Banished Privateer', role: 'AHOY', img: F('Thomas-Tew') },
  { name: 'Unaimed', role: 'TNA', img: HEART1 },
];

const FEARED: PlayerRow[] = [
  { name: 'Engraved', role: 'VP', img: F('dutch-flag') },
  { name: 'Thonys', role: 'GWC', img: F('dutch-flag') },
  { name: 'Captain Mainwairing', role: 'GWC', img: F('dutch-flag') },
];

function PlayerList({
  rows,
  lead,
  links,
}: {
  rows: PlayerRow[];
  lead?: boolean;
  links?: Map<string, string>;
}) {
  return (
    <ul className={lead ? 'players lead' : 'players'}>
      {rows.map((r, i) => {
        const slug = links?.get(r.name);
        return (
          <li data-reveal key={`${r.name}-${i}`}>
            {r.img && <img src={r.img} alt="" loading="lazy" />}
            {slug ? (
              <Link className="pn" to={`/crew/${slug}`}>
                {r.name}
              </Link>
            ) : (
              <span className="pn">{r.name}</span>
            )}
            {r.role && <span className="ps">{r.role}</span>}
          </li>
        );
      })}
    </ul>
  );
}

const YT_VIDEO_ID = 'ypa3zuNzaRk';

export function AboutPage() {
  const articlesRef = useRef<HTMLElement | null>(null);
  const videoRef = useRef<HTMLElement | null>(null);
  const hallRef = useRef<HTMLElement | null>(null);
  const membersRef = useRef<HTMLElement | null>(null);

  const filmRef = useRef<HTMLAnchorElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ccTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  useEffect(() => () => ccTimersRef.current.forEach(clearTimeout), []);

  const [iframeMounted, setIframeMounted] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);

  // `undefined` while loading AND on failure, not just on failure — so the
  // Hall of Members never jumps from the hard-coded rows to live ones
  // mid-load; it only ever swaps once, from fallback to a loaded roster.
  const rosterQuery = useQuery({ queryKey: ['roster'], queryFn: getRoster, retry: false });
  const roster = rosterQuery.isSuccess ? rosterQuery.data : undefined;

  useReveal(videoRef);
  useReveal(hallRef);
  useReveal(membersRef);

  // the articles: each written in like a quill stroke as it comes into
  // view — a faint gold numeral scales in, then the value text wipes on
  // via a clip-path sweep. Once per item (build.py's `once:true`), nothing
  // pins the scroll.
  useGsapContext(
    articlesRef,
    () => {
      if (reduceMotion) return;
      const items = gsap.utils.toArray<HTMLLIElement>('.values li', articlesRef.current ?? undefined);
      items.forEach((li) => {
        const vn = li.querySelector<HTMLElement>('.vn');
        const vt = li.querySelector<HTMLElement>('.vt');
        const tl = gsap.timeline({ scrollTrigger: { trigger: li, start: 'top 82%', once: true } });
        if (vn) tl.from(vn, { opacity: 0, scale: 1.12, duration: 1.2, ease: 'expo.out' });
        if (vt) {
          tl.fromTo(
            vt,
            { clipPath: 'inset(-10% 100% -10% 0)' },
            { clipPath: 'inset(-10% 0% -10% 0)', duration: 1.3, ease: 'power2.inOut' },
            0.1,
          );
        }
      });
    },
    [],
  );

  // clear the pending iframe-removal timer if we unmount mid-close
  useEffect(() => {
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  function handleFilmClick(e: MouseEvent<HTMLAnchorElement>) {
    if (e.metaKey || e.ctrlKey || e.shiftKey) return; // let the modified click open the real YouTube page
    e.preventDefault();
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setIframeMounted(true);
  }

  function handleIframeLoad(e: SyntheticEvent<HTMLIFrameElement>) {
    setVideoPlaying(true);
    closeRef.current?.focus();
    // no subtitles: cc_load_policy=0 in the URL, and because a viewer's own YouTube
    // setting can still switch them on, unload the captions module once the player
    // (and its captions, which load a moment after playback starts) is up
    const win = e.currentTarget.contentWindow;
    const unloadCaptions = () =>
      win?.postMessage(JSON.stringify({ event: 'command', func: 'unloadModule', args: ['captions'] }), 'https://www.youtube-nocookie.com');
    ccTimersRef.current.forEach(clearTimeout);
    ccTimersRef.current = [0, 1000, 3000].map((ms) => setTimeout(unloadCaptions, ms));
  }

  function handleClose() {
    ccTimersRef.current.forEach(clearTimeout);
    setVideoPlaying(false);
    filmRef.current?.focus();
    closeTimerRef.current = setTimeout(() => setIframeMounted(false), 800);
  }

  return (
    <>
      <Opener lines={['About', 'Clan Heart']} />
      <section className="articles" id="articles" aria-labelledby="articles-h" ref={articlesRef}>
        <div className="art-pin wrap">
          <h2 className="label art-h" id="articles-h">
            The Articles of Clan Heart
          </h2>
          <ol className="values">
            {VALUES.map((v, i) => (
              <li data-reveal key={v}>
                <span className="vn" aria-hidden="true">
                  {ROMAN[i]}
                </span>
                <span className="vt">{v.charAt(0).toUpperCase() + v.slice(1)}</span>
              </li>
            ))}
          </ol>
        </div>
      </section>
      <section className={`banner${videoPlaying ? ' playing' : ''}`} aria-label="Clan video" ref={videoRef}>
        {/* parchment painted outside a waving torn flag shape, over the video: the video itself waves */}
        <BannerCanvas kind="frame" tint="#F4E1C0" />
        <div
          className="yt"
          id="yt"
          style={{ backgroundImage: `url(https://i.ytimg.com/vi/${YT_VIDEO_ID}/maxresdefault.jpg)` }}
        >
          {iframeMounted && (
            <iframe
              title="Invitation to Clan Heart, Naval Action"
              src={`https://www.youtube-nocookie.com/embed/${YT_VIDEO_ID}?autoplay=1&controls=0&rel=0&playsinline=1&modestbranding=1&iv_load_policy=3&cc_load_policy=0&enablejsapi=1`}
              allow="autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
              onLoad={handleIframeLoad}
            />
          )}
        </div>
        <a
          className="film"
          id="film"
          ref={filmRef}
          href={`https://www.youtube.com/watch?v=${YT_VIDEO_ID}`}
          data-reveal
          onClick={handleFilmClick}
        >
          <span className="play" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="28" height="28">
              <path d="M8 5v14l11-7z" fill="currentColor" />
            </svg>
          </span>
          <span className="label">Watch the clan video</span>
        </a>
        {iframeMounted && (
          <button type="button" className="yt-close" ref={closeRef} onClick={handleClose}>
            Close video
          </button>
        )}
      </section>
      <section className="hall wrap" aria-labelledby="hall-h" ref={hallRef}>
        <h2 className="h2 hall-h" id="hall-h" data-reveal>
          Hall of Members
        </h2>
      </section>
      <section className="wrap section" aria-label="Members" ref={membersRef}>
        <PlayerList rows={rosterRowsFor('officers', roster, OFFICERS)} links={profileLinksFor('officers', roster)} lead />
        <PlayerList rows={rosterRowsFor('members', roster, MEMBERS)} links={profileLinksFor('members', roster)} />
        <p className="join-note" data-reveal>
          Want to be listed among here as well? <Link to="/join">Join our Ranks!</Link>
        </p>
        <h2 className="h2 sub" data-reveal>
          Honorable Mentions
        </h2>
        <PlayerList rows={rosterRowsFor('honorable', roster, HONORABLE)} links={profileLinksFor('honorable', roster)} />
        <h2 className="h2 sub" data-reveal>
          Respected Dockmates
        </h2>
        <PlayerList rows={rosterRowsFor('dockmates', roster, DOCKMATES)} links={profileLinksFor('dockmates', roster)} />
        <h2 className="h2 sub" data-reveal>
          Feared Sailors
        </h2>
        <PlayerList rows={rosterRowsFor('feared', roster, FEARED)} links={profileLinksFor('feared', roster)} />
      </section>
    </>
  );
}
